<template>
    <div class="email-main-div">
        <div class="divider"></div>
        <div class="verification-code-label">
            <p id="email-otp-label">Verification Code</p>
        </div>
        <div class="otp-div" role="group" aria-labelledby="email-otp-label">
            <PrimeInputOtp
                integerOnly
                v-model="otpValue"
                :length="6"
                size="xs"
                class="input-otp"
                @update:modelValue="updateOtpValue"
                @keydown.enter="onEnter"
                @paste="onPaste"
            >
                <template #default="{ attrs, events, index }">
                    <input
                        type="text"
                        v-bind="attrs"
                        v-on="events"
                        :aria-label="`Verification code digit ${index} of 6`"
                        inputmode="numeric"
                        autocomplete="one-time-code"
                        class="custom-otp-input"
                    />
                    <span v-if="index === 3" class="otp-dash" aria-hidden="true">-</span>
                </template>
            </PrimeInputOtp>
        </div>
        <div class="resend-section">
            <p>
                Didn't get a code?
                <button
                    type="button"
                    :disabled="isResendDisabled"
                    :class="{
                        'text-blue-600': !isEmailSent && !isResendDisabled,
                        'text-gray-600': isEmailSent || isResendDisabled,
                        underline: true,
                        'cursor-pointer': !isResendDisabled,
                        'cursor-not-allowed': isResendDisabled,
                    }"
                    @click="resendEmail"
                >
                    Click to resend
                </button>
            </p>
        </div>
        <div class="divider my-3" v-if="isEmailSent || store?.errorMessage"></div>
        <!-- Mounted throughout and sr-only while idle: a polite region that appears already
             holding its text is not reliably announced. -->
        <div :class="isEmailSent ? 'otp-resend-message' : 'sr-only'" role="status">
            <template v-if="isEmailSent">
                <CheckCircleIcon class="check-icon" aria-hidden="true" />
                <p>Verification code re-sent!</p>
            </template>
        </div>
        <div class="validation-message" v-if="store?.errorMessage" role="alert">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path
                    d="M10 1.875C8.39303 1.875 6.82214 2.35152 5.486 3.24431C4.14985 4.1371 3.10844 5.40605 2.49348 6.8907C1.87852 8.37535 1.71762 10.009 2.03112 11.5851C2.34463 13.1612 3.11846 14.6089 4.25476 15.7452C5.39106 16.8815 6.8388 17.6554 8.4149 17.9689C9.99099 18.2824 11.6247 18.1215 13.1093 17.5065C14.594 16.8916 15.8629 15.8502 16.7557 14.514C17.6485 13.1779 18.125 11.607 18.125 10C18.1227 7.84581 17.266 5.78051 15.7427 4.25727C14.2195 2.73403 12.1542 1.87727 10 1.875ZM9.375 6.25C9.375 6.08424 9.44085 5.92527 9.55806 5.80806C9.67527 5.69085 9.83424 5.625 10 5.625C10.1658 5.625 10.3247 5.69085 10.4419 5.80806C10.5592 5.92527 10.625 6.08424 10.625 6.25V10.625C10.625 10.7908 10.5592 10.9497 10.4419 11.0669C10.3247 11.1842 10.1658 11.25 10 11.25C9.83424 11.25 9.67527 11.1842 9.55806 11.0669C9.44085 10.9497 9.375 10.7908 9.375 10.625V6.25ZM10 14.375C9.81458 14.375 9.63333 14.32 9.47916 14.217C9.32499 14.114 9.20482 13.9676 9.13387 13.7963C9.06291 13.625 9.04434 13.4365 9.08052 13.2546C9.11669 13.0727 9.20598 12.9057 9.33709 12.7746C9.4682 12.6435 9.63525 12.5542 9.81711 12.518C9.99896 12.4818 10.1875 12.5004 10.3588 12.5714C10.5301 12.6423 10.6765 12.7625 10.7795 12.9167C10.8825 13.0708 10.9375 13.2521 10.9375 13.4375C10.9375 13.6861 10.8387 13.9246 10.6629 14.1004C10.4871 14.2762 10.2486 14.375 10 14.375Z"
                    fill="currentColor"
                />
            </svg>
            <span>{{ store?.errorMessage }}</span>
        </div>
    </div>
</template>
<script setup lang="ts">
import { CheckCircleIcon } from '@heroicons/vue/24/solid';
import { ref } from 'vue';
import { ModalNames } from '~/types/modals';

const emit = defineEmits(['update:otpValue', 'enterPressed']);
const onEnter = () => {
    emit('enterPressed');
};

const isEmailSent = ref(false);
const isInvalidCode = ref(false);
const otpValue = ref('');
const isResendDisabled = ref(false);

const onPaste = (event: ClipboardEvent) => {
    setTimeout(() => {
        onEnter();
    }, 0);
};

const updateOtpValue = (value: string) => {
    otpValue.value = value;
    emit('update:otpValue', value);
};

//use dialog ref
const dialogRef = getDialogRef(ModalNames.EMAIL_VERIFICATION);

const store = usePersonStore();
const { handleClearToast } = useGlobalToast();

defineExpose({ isEmailSent });


const resendEmail = async () => {
    if (isResendDisabled.value) return;
    
    handleClearToast();
    store.errorMessage = '';
    otpValue.value = '';
    emit('update:otpValue', '');

    isEmailSent.value = true;
    isResendDisabled.value = true;
    await store.sendEmailVerificationCode(dialogRef.value.data.email, true);

    setTimeout(() => {
        isResendDisabled.value = false;
        isEmailSent.value = false;
    }, 30000);
};
</script>
<style lang="scss" scoped>
.email-main-div {
    @apply flex flex-col items-start gap-1.5 pt-4 self-stretch;
}
.divider {
    @apply h-px self-stretch bg-zinc-300;
}
.verification-code-label {
    @apply flex flex-col items-start gap-1.5 self-stretch pt-3;

    p {
        @apply text-gray-700 text-sm font-medium leading-5;
    }
}
.otp-div {
    @apply flex items-center gap-2 w-full;
}
.resend-section {
    @apply flex items-start;

    p {
        @apply text-zinc-500 text-sm font-normal leading-5;
    }

    // Strip the UA button chrome so the real control renders exactly like the inline text it replaces.
    button {
        @apply bg-transparent border-0 p-0 text-left align-baseline;
        font: inherit;
    }
}
.otp-resend-message {
    @apply flex items-center gap-2 my-1 self-stretch;

    .check-icon {
        @apply w-5 h-5 text-green-500;
    }
    p {
        @apply text-gray-600 text-sm font-normal leading-5;
    }
}
.validation-message {
    @apply flex gap-1;
    color: var(--danger-mark);

    span {
        @apply text-red-600 text-sm font-normal leading-5;
    }
}
</style>
